import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ConnectionType } from '@capacitor/network';
import { modalController } from '@ionic/core';
import { Env } from '@stencil/core';
import { CoreoAPI } from '../../services/api.service';

import AppDatabase, { ProjectAccessDeniedError, ProjectFreeTrialExpiredError, ProjectSyncPostCommitError, ProjectSyncStep } from '../../services/db/app-db.service';
import {
  clearDb,
  FileSystemService,
  uninstallProject
} from '../../services/db/filesystem.service';
import SQLDatabase from '../../services/db/sql.service';
import MapService from '../../services/maps.service';
import { OfflineMapsService } from '../../services/offline-maps.service';
import ProjectDownloadService from '../../services/project-download.service';

import theme from '../../services/theme.service';
import { CoreoApp, CoreoAppSummary, CoreoProjectFeatures, CreateProjectOptions } from '../../types';
import { TypeKeys } from '../actions';
import { recordsClearFilter } from '../records/records.actions';
import { ApplicationState } from '../reducers';
import {
  getAppConfigApiUrl,
  getAppConfig,
  getAppId,
  getAppLocalDatabaseName,
  getAppPage,
  getAppWelcomePageId,
  getAuthToken,
  getCurrentAppWelcomeLastSeen
} from '../selectors';

import { ProjectService } from '../../services/projects.service';
import { AppEnvironment, AppSettings, AppSyncState } from './app.reducer';

export interface AppInitAction {
  type: TypeKeys.APP_INIT;
}

export interface AppLoadAction {
  type: TypeKeys.APP_LOAD;
  id: number;
  switch: boolean;
}

export interface AppLoadSuccessAction {
  type: TypeKeys.APP_LOAD_SUCCESS;
  app: CoreoApp;
}

export interface AppLoadFailureAction {
  type: TypeKeys.APP_LOAD_FAILURE;
  err: any;
}

export interface AppInstallAction {
  type: TypeKeys.APP_INSTALL;
  id: number;
}

export interface AppInstallSuccessAction {
  type: TypeKeys.APP_INSTALL_SUCCESS;
  app: CoreoAppSummary;
  timestamp: number;
}

export interface AppInstallFailureAction {
  type: TypeKeys.APP_INSTALL_FAILURE;
  err: any;
}

export interface AppUninstallAction {
  type: TypeKeys.APP_UNINSTALL;
  id: number;
}

export interface AppTrialExpiredAction {
  type: TypeKeys.APP_TRIAL_EXPIRED;
  id: number;
}

export interface AppAccessDeniedAction {
  type: TypeKeys.APP_ACCESS_DENIED;
  id: number;
}

export interface AppAvailableAction {
  type: TypeKeys.APP_AVAILABLE;
  id: number;
}
export interface AppWelcomeSeenAction {
  type: TypeKeys.APP_WELCOME_SEEN;
  id: number;
  pageId: number;
}

export interface AppDismissTutorial {
  type: TypeKeys.APP_DISMISS_TUTORIAL;
}

export interface AppsResetAction {
  type: TypeKeys.APPS_RESET;
}

export interface AppDownloadMediaAction {
  type: TypeKeys.APP_DOWNLOAD_MEDIA;
  id: number;
  progress?: number;
}

export interface AppDownloadMediaSuccessAction {
  type: TypeKeys.APP_DOWNLOAD_MEDIA_SUCCESS;
  id: number;
}
export interface AppDownloadMediaErrorAction {
  type: TypeKeys.APP_DOWNLOAD_MEDIA_ERROR;
  id: number;
  error: any;
}

export interface AppDownloadAttachmentsAction {
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS;
  id: number;
  progress?: number;
}

export interface AppDownloadAttachmentsSuccessAction {
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS_SUCCESS;
  id: number;
}
export interface AppDownloadAttachmentsErrorAction {
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS_ERROR;
  id: number;
  error: any;
}

/**
 * Which part of the map download a maps action refers to: the blocking image/geojson
 * layers awaited during install, or the background raster-tile pyramids.
 */
export type MapDownloadPhase = 'blocking' | 'raster';

export interface AppDownloadMapsAction {
  type: TypeKeys.APP_DOWNLOAD_MAPS;
  id: number;
  progress?: number;
  phase?: MapDownloadPhase;
}

export interface AppDownloadMapsSuccessAction {
  type: TypeKeys.APP_DOWNLOAD_MAPS_SUCCESS;
  id: number;
  phase?: MapDownloadPhase;
}

export interface AppDownloadMapsErrorAction {
  type: TypeKeys.APP_DOWNLOAD_MAPS_ERROR;
  id: number;
  error: any;
  phase?: MapDownloadPhase;
}

export interface AppSwitchEnvironmentAction {
  type: TypeKeys.APP_SWITCH_ENVIRONMENT;
  env?: AppEnvironment;
}

export interface AppSyncAction {
  type: TypeKeys.APP_SYNC;
  id: number;
  force?: boolean;
}

export interface AppSyncSuccessAction {
  type: TypeKeys.APP_SYNC_SUCCESS;
  id: number;
  timestamp: number;
}

export interface AppSyncErrorAction {
  type: TypeKeys.APP_SYNC_ERROR;
  id: number;
  error: any;
}

export interface AppUpdateSettingsAction {
  type: TypeKeys.APP_UPDATE_SETTINGS;
  settings: Partial<AppSettings>;
};

export interface AppUpdateNetworkAction {
  type: TypeKeys.APP_UPDATE_NETWORK;
  networkConnected: boolean;
  networkConnectionType: ConnectionType;
}

export interface AppUpdateSyncStateAction {
  type: TypeKeys.APP_SYNC_STATE_UPDATE;
  id: number;
  syncState: AppSyncState;
}

export interface AppUpdateFeaturesAction {
  type: TypeKeys.APP_FEATURES_UPDATE;
  id: number;
  features: CoreoProjectFeatures;
}

export interface AppCreateProjectAction {
  type: TypeKeys.APP_CREATE_PROJECT;
};

export interface AppCreateProjectSuccessAction {
  type: TypeKeys.APP_CREATE_PROJECT_SUCCESS;
  projectId: number;
}

export interface AppCreateProjectErrorAction {
  type: TypeKeys.APP_CREATE_PROJECT_ERROR;
  error: any;
}

export type AppActions =
  | AppInitAction
  | AppLoadAction
  | AppLoadSuccessAction
  | AppLoadFailureAction
  | AppInstallAction
  | AppInstallSuccessAction
  | AppInstallFailureAction
  | AppUninstallAction
  | AppDismissTutorial
  | AppWelcomeSeenAction
  | AppsResetAction
  | AppDownloadMediaAction
  | AppDownloadMediaSuccessAction
  | AppDownloadMediaErrorAction
  | AppSwitchEnvironmentAction
  | AppSyncAction
  | AppSyncSuccessAction
  | AppSyncErrorAction
  | AppTrialExpiredAction
  | AppAvailableAction
  | AppAccessDeniedAction
  | AppDownloadAttachmentsAction
  | AppDownloadAttachmentsSuccessAction
  | AppDownloadAttachmentsErrorAction
  | AppDownloadMapsAction
  | AppDownloadMapsSuccessAction
  | AppDownloadMapsErrorAction
  | AppUpdateSettingsAction
  | AppUpdateNetworkAction
  | AppUpdateSyncStateAction
  | AppUpdateFeaturesAction
  | AppCreateProjectAction
  | AppCreateProjectSuccessAction
  | AppCreateProjectErrorAction
  ;

export const appInit = (): AppInitAction => ({
  type: TypeKeys.APP_INIT
});

export const appTrialExpired = (id: number): AppTrialExpiredAction => ({
  type: TypeKeys.APP_TRIAL_EXPIRED,
  id
});

export const appAccessDenied = (id: number): AppAccessDeniedAction => ({
  type: TypeKeys.APP_ACCESS_DENIED,
  id
});

export const appAvailable = (id: number): AppAvailableAction => ({
  type: TypeKeys.APP_AVAILABLE,
  id
});

export const appLoad = (id: number, isSwitch: boolean = false): AppLoadAction => ({
  type: TypeKeys.APP_LOAD,
  id,
  switch: isSwitch
});

export const appLoadSuccess = (app: CoreoApp): AppLoadSuccessAction => ({
  type: TypeKeys.APP_LOAD_SUCCESS,
  app
});

export const appLoadFailure = (err: string): AppLoadFailureAction => ({
  type: TypeKeys.APP_LOAD_FAILURE,
  err
});

export const appInstallApp = (id: number): AppInstallAction => ({
  type: TypeKeys.APP_INSTALL,
  id
});

export const appInstallAppSuccess = (app: CoreoAppSummary): AppInstallSuccessAction => ({
  type: TypeKeys.APP_INSTALL_SUCCESS,
  app,
  timestamp: Date.now()
});

export const appInstallAppFailure = (err: any): AppInstallFailureAction => ({
  type: TypeKeys.APP_INSTALL_FAILURE,
  err
});

export const appUninstallApp = (id: number): AppUninstallAction => ({
  type: TypeKeys.APP_UNINSTALL,
  id
});

export const appDismissTutorial = (): AppDismissTutorial => ({
  type: TypeKeys.APP_DISMISS_TUTORIAL
});

export const appWelcomeSeen = (id: number, pageId: number): AppWelcomeSeenAction => ({
  type: TypeKeys.APP_WELCOME_SEEN,
  id,
  pageId
});

export const appDownloadMedia = (id: number, progress?: number): AppDownloadMediaAction => ({
  type: TypeKeys.APP_DOWNLOAD_MEDIA,
  id,
  progress
});

export const appDownloadMediaSuccess = (id: number): AppDownloadMediaSuccessAction => ({
  type: TypeKeys.APP_DOWNLOAD_MEDIA_SUCCESS,
  id
});

export const appDownloadMediaError = (id: number, error: any): AppDownloadMediaErrorAction => ({
  type: TypeKeys.APP_DOWNLOAD_MEDIA_ERROR,
  id,
  error
});

export const appDownloadAttachments = (id: number, progress?: number): AppDownloadAttachmentsAction => ({
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS,
  id,
  progress
});

export const appDownloadAttachmentsSuccess = (id: number): AppDownloadAttachmentsSuccessAction => ({
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS_SUCCESS,
  id
});

export const appDownloadAttachmentsError = (id: number, error: any): AppDownloadAttachmentsErrorAction => ({
  type: TypeKeys.APP_DOWNLOAD_ATTACHMENTS_ERROR,
  id,
  error
});

export const appDownloadMaps = (id: number, progress?: number, phase?: MapDownloadPhase): AppDownloadMapsAction => ({
  type: TypeKeys.APP_DOWNLOAD_MAPS,
  id,
  progress,
  phase
});

export const appDownloadMapsSuccess = (id: number, phase?: MapDownloadPhase): AppDownloadMapsSuccessAction => ({
  type: TypeKeys.APP_DOWNLOAD_MAPS_SUCCESS,
  id,
  phase
});

export const appDownloadMapsError = (id: number, error: any, phase?: MapDownloadPhase): AppDownloadMapsErrorAction => ({
  type: TypeKeys.APP_DOWNLOAD_MAPS_ERROR,
  id,
  error,
  phase
});

export const appUpdateSyncState = (id: number, syncState: AppSyncState): AppUpdateSyncStateAction => ({
  type: TypeKeys.APP_SYNC_STATE_UPDATE,
  id,
  syncState
});

export const appUpdateFeatures = (id: number, features: CoreoProjectFeatures): AppUpdateFeaturesAction => ({
  type: TypeKeys.APP_FEATURES_UPDATE,
  id,
  features
});

export const appCreateProject = (): AppCreateProjectAction => ({
  type: TypeKeys.APP_CREATE_PROJECT
});

export const appCreateProjectSuccess = (projectId: number): AppCreateProjectSuccessAction => ({
  type: TypeKeys.APP_CREATE_PROJECT_SUCCESS,
  projectId
});

export const appCreateProjectError = (error: any): AppCreateProjectErrorAction => ({
  type: TypeKeys.APP_CREATE_PROJECT_ERROR,
  error
});

export const appSwitchEnvironment = (env?: AppEnvironment) => async (dispatch, getState) => {
  dispatch({
    type: TypeKeys.APP_SWITCH_ENVIRONMENT,
    env
  });
  const state = getState();

  const apiUrl = getAppConfigApiUrl(state);
  const dbName = getAppLocalDatabaseName(state);

  CoreoAPI.instance.setApiUrl(apiUrl);
  CoreoAPI.instance.setAuthToken(null);

  await SQLDatabase.instance.init(dbName);
  await AppDatabase.instance.init();
  // SQLDatabase.init closes every connection, the offline tile database's included.
  await OfflineMapsService.instance.openTileDb();
};

export const appSync = (id: number, force?: boolean): AppSyncAction => ({
  type: TypeKeys.APP_SYNC,
  id,
  force,
});

export const appSyncSuccess = (id: number, timestamp: number = new Date().valueOf()): AppSyncSuccessAction => ({
  type: TypeKeys.APP_SYNC_SUCCESS,
  id,
  timestamp
});

export const appSyncError = (id: number, error: any): AppSyncErrorAction => ({
  type: TypeKeys.APP_SYNC_ERROR,
  id,
  error
});

export const appUpdateSettings = (settings: Partial<AppSettings>): AppUpdateSettingsAction => ({
  type: TypeKeys.APP_UPDATE_SETTINGS,
  settings
});

export const appUpdateNetwork = (networkConnected: boolean, networkConnectionType: ConnectionType): AppUpdateNetworkAction => ({
  type: TypeKeys.APP_UPDATE_NETWORK,
  networkConnected,
  networkConnectionType
});

export const init = async (dispatch, getState) => {
  const state = getState();
  const appId = getAppId(state);

  // Initialize the API Service
  const apiUrl = getAppConfigApiUrl(state);
  const authToken = getAuthToken(state);

  CoreoAPI.instance.setApiUrl(apiUrl);
  CoreoAPI.instance.setAuthToken(authToken);

  if (Capacitor.isNativePlatform()) {
    const appInfo = await App.getInfo();
    CoreoAPI.instance.setAppDetails(appInfo.version, `${appInfo.build} (${Env.commit})`);
  } else {
    CoreoAPI.instance.setAppDetails('web', Env.commit);
  }

  const databaseName = getAppLocalDatabaseName(state);
  await SQLDatabase.instance.init(databaseName);
  await AppDatabase.instance.init();
  await FileSystemService.instance.init();
  await OfflineMapsService.instance.init();
  await MapService.instance.init();

  // Only attempt to load an app if we have completed offline migration

  if (appId !== null) {
    await loadApp(appId)(dispatch, getState);
  }
  dispatch(appInit());
};


export const loadApp = (id: number, showWelcome = true) => async (dispatch, getState) => {

  const state: ApplicationState = getState();
  const isSwitch = id !== state.app.appId;
  dispatch(appLoad(id, isSwitch));
  dispatch(recordsClearFilter());

  try {
    await FileSystemService.instance.initProjectFilesystem(id);
    const app: CoreoApp = await AppDatabase.instance.loadProject(id);

    // Update the theme
    theme.reset();
    theme.update(app);


    dispatch(appLoadSuccess(app));

    if (showWelcome) {
      dispatch(showAppWelcomeIfNeeded(id));
    }

    // Premium basemap packs wait for the plan features loaded above.
    void OfflineMapsService.instance.resumeDownloads();

    // Resume GeoTIFF / raster-tile download if the local pyramid is incomplete
    ProjectDownloadService.instance.downloadRasterTiles(id, { skipIfIdle: true }).catch(err => {
      if (err?.name !== 'AbortError' && err?.message !== 'This operation was aborted') {
        console.warn('Raster tile download failed:', err);
      }
    });
  } catch (e) {
    console.error(e)
    dispatch(appLoadFailure(e));
  }
};

export const reloadApp = (app: CoreoApp, showWelcome = true) => async (dispatch) => {
  dispatch(appLoad(app.id));

  try {
    // Update the theme
    theme.reset();
    theme.update(app);

    dispatch(appLoadSuccess(app));

    if (showWelcome) {
      dispatch(showAppWelcomeIfNeeded(app.id));
    }
  } catch (e) {
    console.error(e)
    dispatch(appLoadFailure(e));
  }
};


export const uninstallApp = (id: number) => async (dispatch) => {
  // Stop background downloads (raster tiles, media, attachments) and wait for them to
  // settle so nothing is written into the project directory once it is deleted.
  await ProjectDownloadService.instance.cancel(id);
  await uninstallProject(id);
  await AppDatabase.instance.deleteProject(id);
  dispatch(appUninstallApp(id));
};

export const refreshAppStatus = (id: number) => async (dispatch) => {
  try {
    const project = await AppDatabase.instance.getProjectFromAPI(id);

    if (!project.freeTrialExpired) {
      dispatch(appAvailable(id));
      return true;
    }
    return false;
  } catch (e) {
    console.warn(e);
    return false;
  }
}

export const appsReset = () => async dispatch => {
  await clearDb();
  await SQLDatabase.instance.dropAllTables();
  dispatch({
    type: TypeKeys.APPS_RESET
  });
};

export const showAppWelcomeIfNeeded = (id: number) => async (dispatch, getState) => {
  const state = getState();
  const welcomeLastSeen = getCurrentAppWelcomeLastSeen(state);
  const welcomePageId = getAppWelcomePageId(state);
  if (welcomePageId === null || !welcomeLastSeen) {
    return;
  }
  const welcomePage = getAppPage(welcomePageId)(state);
  if (!welcomePage) {
    return;
  }

  const welcomePageLastUpdated = welcomePage.updatedAt;

  if (
    welcomeLastSeen.timestamp === null
    || new Date(welcomePageLastUpdated) > new Date(welcomeLastSeen.timestamp)
    || welcomePageId !== welcomeLastSeen.id) {
    setTimeout(async () => {
      const modal = await modalController.create({
        component: 'app-page',
        componentProps: {
          pageId: welcomePageId,
          showCloseFooter: true
        }
      });
      modal.present();
      dispatch(appWelcomeSeen(id, welcomePageId));
    }, 250);
  }
}

/**
 * Media / attachment downloads run through ProjectDownloadService so they share the
 * per-project abort + cancel() lifecycle that uninstallApp waits on before deleting
 * the project's files. The service handles the already-downloading check and the
 * start/success dispatches.
 */
export const downloadMedia = (id: number) => async () => {
  await ProjectDownloadService.instance.downloadMedia(id);
};

export const downloadAttachments = (id: number) => async () => {
  await ProjectDownloadService.instance.downloadAttachments(id);
};

// NOTE: downloadMapImagery and downloadRasterTiles have been moved to ProjectDownloadService

export interface SyncProjectOptions {
  force?: boolean;
  reload?: boolean;
  step?: ProjectSyncStep | null;
  /** When true, sync modal may offer navigation to /issues after a validation upload failure. */
  showIssuesLink?: boolean;
}

export const syncProject = (id: number, options: SyncProjectOptions = { force: false, reload: true, step: null }) => async (dispatch, getState) => {
  const force = options.force ?? false;
  const reload = options.reload ?? true;

  dispatch(appSync(id, force));

  try {
    const app = await AppDatabase.instance.syncProject(id, {
      force,
      step: options.step ?? undefined,
      showIssuesLink: options.showIssuesLink
    });
    dispatch(appSyncSuccess(id));

    if (app && reload) {
      await reloadApp(app)(dispatch);
    }

    // Start background downloads (fire and forget)
    ProjectDownloadService.instance.downloadMaps(id).catch(err => {
      if (err?.name !== 'AbortError' && err?.message !== 'This operation was aborted') {
        console.warn('Map download failed:', err);
      }
    });
    ProjectDownloadService.instance.downloadMedia(id);
    ProjectDownloadService.instance.downloadAttachments(id);
  } catch (e) {
    if (e instanceof ProjectSyncPostCommitError) {
      if (reload) {
        await loadApp(id)(dispatch, getState);
      }
      handleSyncError(dispatch, id, e.cause);
      throw e.cause;
    }
    handleSyncError(dispatch, id, e);
    throw e;
  }
};

/**
 * Handle sync/install errors - dispatch appropriate error actions
 */
const handleSyncError = (dispatch: (action: any) => void, id: number, e: any) => {
  console.warn('Sync error:', e);
  dispatch(appSyncError(id, e));

  if (e instanceof ProjectFreeTrialExpiredError) {
    dispatch(appTrialExpired(id));
  }
  if (e instanceof ProjectAccessDeniedError) {
    dispatch(appAccessDenied(id));
  }
};

export const installProject = (id: number) => async (dispatch, getState) => {
  dispatch(appSync(id, true));
  let syncCompleted = false;

  try {
    // Sync project data
    const app = await AppDatabase.instance.syncProject(id, { force: true });
    syncCompleted = true;

    // Image/geojson layers have no native fallback — wait for those before install completes.
    // Raster-tile pyramids can be hundreds of MB; download them in the background and use
    // the CDN until the local manifest is written.
    await ProjectDownloadService.instance.downloadBlockingMaps(id);

    // Now mark installation as complete
    dispatch(appInstallAppSuccess(app));
    dispatch(appSyncSuccess(id));

    // Start background downloads (fire and forget)
    ProjectDownloadService.instance.downloadRasterTiles(id).catch(err => {
      if (err?.name !== 'AbortError' && err?.message !== 'This operation was aborted') {
        console.warn('Raster tile download failed:', err);
      }
    });
    ProjectDownloadService.instance.downloadMedia(id);
    ProjectDownloadService.instance.downloadAttachments(id);
  } catch (e) {
    if (e instanceof ProjectSyncPostCommitError) {
      await loadApp(id)(dispatch, getState);
    }
    const errForHandlers = e instanceof ProjectSyncPostCommitError ? e.cause : e;
    handleSyncError(dispatch, id, errForHandlers);

    // If sync completed but installation failed (e.g., abort during map download),
    // clean up the database and files to prevent orphaned data
    if (syncCompleted) {
      try {
        await ProjectDownloadService.instance.cancel(id);
        await uninstallProject(id);
        await AppDatabase.instance.deleteProject(id);
      } catch (cleanupError) {
        console.warn('Failed to cleanup after installation error:', cleanupError);
      }
    }
  }
};

export const createProjectFromTemplate = (options: CreateProjectOptions) => async (dispatch, getState): Promise<number> => {
  dispatch(appCreateProject());
  try {
    if (options.templateId === 'ukhab') {
      options = {
        ...options,
        androidAppUrl: 'ukhabappv3',
        imageUrl: 'https://coreo.s3-eu-west-1.amazonaws.com/projectImages/imageUrl_1712240403015',
        parentProjectId: getAppConfig(getState()).ukhabTemplateParentProjectId,
      };
      delete options.templateId;
      const { projectId } = await ProjectService.instance.createProject(options);
      dispatch(appCreateProjectSuccess(projectId));
      return projectId;
    }

    const { projectId } = await ProjectService.instance.cloneProject(options);
    dispatch(appCreateProjectSuccess(projectId));
    return projectId;
  } catch (e) {
    dispatch(appCreateProjectError(e));
    throw e;
  }
};
